"use client";

import { useState } from "react";

import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import type {
  CalculationExplanation,
  CalculationExplanationSectionId,
} from "@/lib/funnel-planner/calculation-explanation";
import { resolveExplanationEntryText } from "@/lib/funnel-planner/explanation-copy";
import { funnelText } from "@/lib/funnel-planner/i18n";

const SECTION_LABEL_KEYS: Readonly<Record<CalculationExplanationSectionId, string>> = {
  method: "calculationExplanationMethodLabel",
  package: "calculationExplanationPackageLabel",
  seasonal: "calculationExplanationSeasonalLabel",
  range: "calculationExplanationRangeLabel",
  blockers: "calculationExplanationBlockersLabel",
};

const SECTION_FALLBACKS: Readonly<Record<CalculationExplanationSectionId, string>> = {
  method: "Method",
  package: "Plan suggestion",
  seasonal: "Seasonal assumptions",
  range: "Planning range",
  blockers: "What is limiting this estimate",
};

const SECTION_ORDER: readonly CalculationExplanationSectionId[] = [
  "method",
  "package",
  "seasonal",
  "range",
  "blockers",
];

/**
 * NL-014 explanation surface.
 *
 * Renders only the entry ids and allowlisted primitives the explanation builder
 * produced, so no planner free text, contact detail, or raw amount can appear
 * here. The copy states plainly that it is session-only and estimate-only.
 *
 * Every line goes through `resolveExplanationEntryText`, which owns the mapping
 * from explanation id to localized copy and localizes the machine tokens it
 * interpolates, so no contract enum member reaches the visitor raw.
 */
export function CalculationExplanationPanel({
  content,
  explanation,
}: {
  content: FunnelPlannerContent;
  explanation: CalculationExplanation;
}) {
  const [open, setOpen] = useState(false);

  return (
    <section
      aria-label={funnelText(content, "calculationExplanationSectionAria", "Calculation explanation")}
      className="rounded-xl border bg-muted/30 p-4"
      data-calculation-explanation-blocker-count={explanation.blockers.length}
      data-calculation-explanation-has-receipt={explanation.hasReceipt ? "true" : "false"}
      data-funnel-planner-calculation-explanation="true"
    >
      <h3 className="text-base font-bold">
        {funnelText(content, "calculationExplanationTitle", "How this estimate was worked out")}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        {funnelText(
          content,
          "calculationExplanationIntro",
          "A plain record of the method, assumptions, and anything limiting the result.",
        )}
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        {funnelText(
          content,
          "calculationExplanationEstimateOnly",
          "Planning estimate only. It is not a quote and commits nobody to anything.",
        )}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {funnelText(
          content,
          "calculationExplanationSessionNote",
          "Shown for this session only. It is not saved, not shared, and not included in your PDF.",
        )}
      </p>

      <button
        aria-controls="calculation-explanation-details"
        aria-expanded={open}
        className="mt-3 rounded-md border px-3 py-1.5 text-xs font-semibold hover:bg-muted"
        onClick={() => setOpen((previous) => !previous)}
        type="button"
      >
        {open
          ? funnelText(content, "calculationExplanationHide", "Hide the calculation detail")
          : funnelText(content, "calculationExplanationShow", "Show the calculation detail")}
      </button>

      {open ? (
        <div className="mt-3 space-y-3" id="calculation-explanation-details">
          {SECTION_ORDER.map((section) => {
            const entries = explanation.entries.filter((entry) => entry.section === section);
            if (entries.length === 0) return null;

            return (
              <div key={section}>
                <p className="text-xs font-semibold text-muted-foreground">
                  {funnelText(content, SECTION_LABEL_KEYS[section], SECTION_FALLBACKS[section])}
                </p>
                <ul className="mt-1 list-disc text-xs text-foreground" style={{ paddingInlineStart: 18 }}>
                  {entries.map((entry, index) => (
                    <li key={`${entry.id}-${index}`}>
                      {resolveExplanationEntryText(entry, (key, fallback) =>
                        funnelText(content, key, fallback),
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {explanation.blockers.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {funnelText(
                content,
                "calculationExplanationNoBlockers",
                "Nothing is currently limiting this estimate.",
              )}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
